import React, { useCallback, useState } from 'react';
import { DndProvider } from 'react-dnd';
import cloneDeep from 'lodash.clonedeep';

import useReorderDragDropBackend from 'hooks/useReorderDragDropBackend';
import useMedia from 'hooks/useMedia';

import { ReactComponent as Close } from 'icons/icon-close.svg';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import LabeledCheckbox from 'components/base/LabeledCheckbox/LabeledCheckbox';
import ViewItem from './ViewItem';
import ItemPreview from 'components/Selector/DSPSelector/SelectedDSPList/ItemPreview/ItemPreview';
import { TableHeader } from 'components/data-containers/BrowseEntities/shared/types';
import {
  EditViewModal,
  Content,
  CustomListTitle,
  H1,
  Label,
  P,
  BoxColumns,
  BoxLeft,
  CheckboxList,
  DraggedList,
  ButtonWrapper,
} from './CustomizeView.style';

export type TTableHeader = TableHeader & { selected?: boolean };

export type TItemCustomViewExtended =
  | {
      selected?: boolean;
      label: string;
      value: string;
    }
  | TTableHeader;

interface ICustomizeView {
  title: string;
  hideModal: Function;
  formatItems?: (item: TItemCustomViewExtended) => TItemCustomViewExtended;
  saveToApi: (headers: any[]) => void;
  savedItems: TItemCustomViewExtended[];
  allAvailableItems: TItemCustomViewExtended[];
  displayBlockLabel?: string;
  orderBlockLabel?: string;
  isLow?: boolean;
  canTurnOffAll?: boolean;
}

const CustomizeView: React.FC<ICustomizeView> = (props) => {
  const {
    title,
    hideModal,
    saveToApi,
    formatItems,
    savedItems,
    allAvailableItems,
    displayBlockLabel,
    orderBlockLabel,
    isLow,
    canTurnOffAll,
  } = props;

  const isLargeScreen = useMedia('(min-width: 36rem)');

  const markSelectedItems = (
    header: TItemCustomViewExtended
  ): TItemCustomViewExtended => {
    const selectedHeader = ((savedItems && savedItems.length > 0) ||
    canTurnOffAll
      ? savedItems
      : allAvailableItems
    ).find((h: TItemCustomViewExtended) => h.value === header.value);
    return selectedHeader ? { ...header, selected: true } : header;
  };

  const [tempItems, setTempItems] = useState(
    allAvailableItems
      .map((item) => (formatItems ? formatItems(item) : item))
      .map(markSelectedItems)
  );
  const [draggedItems, setDraggedItems] = useState(
    (savedItems && savedItems.length > 0) || canTurnOffAll
      ? savedItems
      : allAvailableItems
  );
  const isMinAllowedHeadersExceeded =
    tempItems.filter((header: TItemCustomViewExtended) => !!header.selected)
      .length <= 1;

  const handleChecked = ({
    value,
    selected,
  }: {
    value: string;
    selected: boolean;
  }): void => {
    if (!selected) {
      const item = tempItems.find(
        (header: TTableHeader) => header.value === value
      );
      if (item) {
        setDraggedItems([
          ...draggedItems,
          {
            ...item,
            selected: true,
          },
        ]);
      }
    } else {
      setDraggedItems(
        draggedItems.filter(
          (header: TItemCustomViewExtended) => header.value !== value
        )
      );
    }
    const selectedTableHeaders = tempItems.map(
      (header: TItemCustomViewExtended) =>
        header.value === value ? { ...header, selected: !selected } : header
    );
    setTempItems(selectedTableHeaders);
  };

  const excludeColumn = (value: string): void => {
    if (draggedItems.length > 1 || canTurnOffAll) {
      setDraggedItems(
        draggedItems.filter(
          (header: TItemCustomViewExtended) => header.value !== value
        )
      );
      setTempItems(
        tempItems.map((header: TItemCustomViewExtended) =>
          header.value === value ? { ...header, selected: false } : header
        )
      );
    }
  };

  const [
    DragDropBackend,
    backendOptions,
    isTouchDevice,
  ] = useReorderDragDropBackend();

  const changeOrder = useCallback(
    (start: number, end: number) => {
      const dragged = cloneDeep(draggedItems);
      dragged.splice(start, 1);
      dragged.splice(end, 0, draggedItems[start]);
      setDraggedItems(dragged);
    },
    [draggedItems]
  );
  const displayBlockTitle =
    displayBlockLabel || (isLargeScreen ? 'Display Columns' : 'Display');
  const orderBlockTitle =
    orderBlockLabel ||
    (isLargeScreen ? 'Order columns left to right' : 'Order info');

  return (
    <EditViewModal isMobile={!isLargeScreen}>
      <Content isMobile={!isLargeScreen}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          className="title"
        >
          <H1>Customize View</H1>
          <Button onClick={(): void => hideModal()}>
            <Close />
          </Button>
        </Box>
        <CustomListTitle>{title}</CustomListTitle>
        <P>Modify what you would like to see and the display order.</P>
        <BoxColumns
          display="flex"
          justifyContent="space-between"
          mb={6}
          isMobile={!isLargeScreen}
        >
          <BoxLeft isLow={isLow}>
            <Label>{displayBlockTitle}</Label>
            <CheckboxList display="flex">
              <div>
                {tempItems.map(
                  (item: TItemCustomViewExtended): JSX.Element => {
                    return (
                      <LabeledCheckbox
                        key={`table-header-${item.label}`}
                        disabled={
                          item.selected &&
                          !canTurnOffAll &&
                          isMinAllowedHeadersExceeded
                        }
                        checkingParameters={{
                          value: item.value,
                          selected: !!item.selected,
                        }}
                        toggleCheckedHandler={handleChecked}
                        label={item.label}
                      />
                    );
                  }
                )}
              </div>
            </CheckboxList>
          </BoxLeft>
          {draggedItems.length > 0 && (
            <Box minWidth="48%">
              <Label>{orderBlockTitle}</Label>
              <CheckboxList display="flex">
                <DndProvider backend={DragDropBackend} options={backendOptions}>
                  <DraggedList isLow={isLow}>
                    {draggedItems.map(
                      (
                        item: TItemCustomViewExtended,
                        index: number
                      ): JSX.Element => (
                        <ViewItem
                          index={index}
                          item={item}
                          key={`selected-item-${item.label}`}
                          changeOrder={changeOrder}
                          handleChecked={excludeColumn}
                          isRemovable={canTurnOffAll || draggedItems.length > 1}
                        />
                      )
                    )}
                    {isTouchDevice && <ItemPreview key="__preview" />}
                  </DraggedList>
                </DndProvider>
              </CheckboxList>
            </Box>
          )}
        </BoxColumns>
      </Content>

      <ButtonWrapper
        display="flex"
        justifyContent="space-between"
        isMobile={!isLargeScreen}
      >
        <Button
          m={2}
          variant="secondary"
          onClick={(): void => hideModal()}
          type="button"
          borderRadius="3px"
        >
          Cancel
        </Button>
        <Button
          m={2}
          variant="secondary"
          onClick={(): void => {
            saveToApi(allAvailableItems);
            hideModal();
          }}
          type="button"
          borderRadius="3px"
        >
          Reset to Default
        </Button>
        <Button
          m={2}
          variant="primary"
          onClick={(): void => {
            saveToApi(draggedItems);
            hideModal();
          }}
          type="button"
          borderRadius="3px"
        >
          Save
        </Button>
      </ButtonWrapper>
    </EditViewModal>
  );
};

export default CustomizeView;
